import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { isWhiteLabel } from "../services/agencyService";
import onboardingService from "../services/onboardingService";
import { OnboardingBlueprint } from "./Onboarding/Types";
import { ReactComponent as NewIcon } from "../assets/icons/new.svg";
import { ReactComponent as TrashIcon } from  "../assets/icons/trash.svg";

const Blueprints: React.FC = () => {
  const [blueprints, setBlueprints] = useState<OnboardingBlueprint[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState<"lifecycle" | "newsletter">("lifecycle");
  const navigate = useNavigate();

  const fetchBlueprints = async () => {
    return onboardingService.listOnboardingBlueprints();
  };

  useEffect(() => {
    fetchBlueprints().then(setBlueprints);
  }, []);

  const handleBlueprintClick = (id: string) => {
    navigate(`/blueprints/${id}`);
  };

  const handleCreateBlueprint = async (blueprintData?: OnboardingBlueprint) => {
    if (isCreating) return; // prevent multiple clicks

    setIsCreating(true);
    try {
      let newBlueprint;

      if (blueprintData) {
        blueprintData.title = `${blueprintData.title} (Copy)`;
        newBlueprint = await onboardingService.createOnboardingBlueprint(blueprintData);
      } else {
        newBlueprint = await onboardingService.createOnboardingBlueprint();
      }

      navigate(`/blueprints/${newBlueprint.id}`);
    } catch (error) {
      console.error("Error creating blueprint:", error);
      setIsCreating(false);
    }
  };

  const deleteBlueprint = async (blueprintId: string) => {
    await onboardingService.deleteOnboardingBlueprint(blueprintId);
    fetchBlueprints().then(setBlueprints);
  };

  const filteredBlueprints = useMemo(
    () => blueprints.filter((bp) => (bp.blueprint_type || "newsletter") === activeTab),
    [blueprints, activeTab]
  );

  return (
    <div>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
      }}>
        <h1>Blueprints</h1>

        <div style={{ display: "inline-block", marginLeft: "auto" }}>
          <button
            className="tertiary-info-button"
            style={{
              cursor: "pointer",
              marginRight: "12px",
            }}
            onClick={(e) => {
              e.stopPropagation();
              navigate("/blueprints/import-strategy");
            }}
          >
            Import Strategy Doc
          </button>

          <button
            className="tertiary-info-button"
            style={{
              cursor: "pointer",
              marginRight: "12px",
            }}
            onClick={(e) => {
              e.stopPropagation();

              navigate("/onboarding?step=1");
            }}
          >
            Build one for me
          </button>

          <button
            className="primary-button"
            onClick={() => handleCreateBlueprint()}
            disabled={isCreating}
          >
            {isCreating ? "Creating..." : "Create from scratch"}
          </button>
        </div>
      </div>

      <p style={{ marginTop: "0", textAlign: "left" }}>
        Blueprints are instructions for how {isWhiteLabel() ? "we fill" : "Potions fills"} out your templates using AI.
      </p>

      <div className="blueprint-tabs" style={{ marginBottom: "16px" }}>
        <button
          className={`blueprint-tab${activeTab === "lifecycle" ? " active" : ""}`}
          onClick={() => setActiveTab("lifecycle")}
        >
          Lifecycle
        </button>
        <button
          className={`blueprint-tab${activeTab === "newsletter" ? " active" : ""}`}
          onClick={() => setActiveTab("newsletter")}
        >
          Newsletter
        </button>
      </div>

      <p style={{ color: "#6b7280", fontSize: "14px", marginTop: 0, marginBottom: "16px" }}>
        {activeTab === "lifecycle"
          ? "One-off email blasts — welcome emails, announcements, offers, and re-engagement."
          : "Longer, multi-section posts — recurring newsletters with curated content."}
      </p>

      {filteredBlueprints.length === 0 && (
        <p style={{ color: "#9ca3af", textAlign: "center", padding: "32px 0" }}>
          No {activeTab} blueprints yet.
        </p>
      )}

      <ul className="newsletter-list">
        {filteredBlueprints.map((blueprint) => (
          <li
            key={blueprint.id}
            className="newsletter-item blueprint-item-with-preview"
            onClick={() => handleBlueprintClick(blueprint.id)}
            style={{ cursor: "pointer" }}
          >
            {blueprint.reference_edition_html && (
              <div className="blueprint-preview-thumb">
                <div className="blueprint-preview-thumb-scaler">
                  <iframe
                    srcDoc={blueprint.reference_edition_html}
                    title={blueprint.title || "Template preview"}
                    sandbox="allow-same-origin"
                    className="blueprint-preview-thumb-iframe"
                  />
                </div>
              </div>
            )}

            <div className="newsletter-item-content-left" style={{ flex: 1 }}>
              <div>
                <span className="newsletter-title">
                  {blueprint.title || "Untitled Blueprint"}
                </span>
                {blueprint.reference_edition_title && (
                  <span style={{ display: "block", fontSize: "13px", color: "#6b7280", marginTop: "2px" }}>
                    Template: {blueprint.reference_edition_title}
                  </span>
                )}
              </div>
            </div>

            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}>
              <span className="list-tag-purple">
                Sections: {blueprint.raw_info ? blueprint.raw_info.length : 0}
              </span>

              <span className="list-tag-gray">
                Created: {new Date(blueprint.created_at).toLocaleDateString()}
              </span>

              <span className="list-tag-gray">
                Updated: {new Date(blueprint.updated_at).toLocaleDateString()}
              </span>

              <span>
                <NewIcon
                  aria-label="Copy to New"
                  style={{
                    width: "2em",
                    height: "2em",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();

                    handleCreateBlueprint(blueprint);
                  }}
                />
              </span>

              <span>
                <TrashIcon
                  aria-label="Delete"
                  style={{
                    width: "2em",
                    height: "2em",
                    marginRight: "12px",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();

                    const confirmed = window.confirm(
                      "Are you sure you want to delete this blueprint? This action cannot be undone."
                    );

                    if (confirmed) {
                      deleteBlueprint(blueprint.id);
                    }
                  }}
                />
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Blueprints;
